import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { isPjRole } from '@/config/routeAccess'
import {
  canViewFinanceClosedScope,
  canViewFinanceFinancialValues,
} from '@/components/Financeiro/financeGestorVisibility'
import { financeService, type FinanceRequestDetail, type FinanceRequestRow } from '@/services/financeService'
import {
  areasForRole,
  type FinanceAreaKey,
  type PortalTypeKey,
} from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import { buildPortalTypesByArea, isPortalTypeVisibleForRole } from '@/components/Financeiro/solicitacoes/portalTypeAccess'
import type { PortalPjTab } from '@/components/layout/PortalPjLayout'
import { isPortalPjCatalogType } from '@/components/PortalPj/portalPjTypeCatalog'
import type { FinanceTypeCard } from '@/hooks/useFinanceiroSolicitacoesPage'
import { portalPjFeedbackService, type PortalPjFeedback } from '@/services/portalPjFeedbackService'

export function usePortalPjPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const role = useAuthStore((s) => s.user?.role) ?? 'usuario_agrada'
  const canUseFeedback = isPjRole(role)

  const rawTab = searchParams.get('tab')
  const tab: PortalPjTab =
    rawTab === 'meus' || rawTab === 'nf' || (rawTab === 'feedback' && canUseFeedback) ? rawTab : 'nova'
  const selectedRequestId = searchParams.get('request')

  const allowedAreas = useMemo(() => areasForRole(role), [role])
  const [types, setTypes] = useState<FinanceTypeCard[]>([])
  const [typesLoading, setTypesLoading] = useState(true)
  const [selectedType, setSelectedType] = useState<PortalTypeKey | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const [requests, setRequests] = useState<FinanceRequestRow[]>([])
  const [requestsLoading, setRequestsLoading] = useState(false)
  const [requestsError, setRequestsError] = useState<string | null>(null)

  const [requestDetail, setRequestDetail] = useState<FinanceRequestDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const [feedbackItems, setFeedbackItems] = useState<PortalPjFeedback[]>([])
  const [feedbackLoading, setFeedbackLoading] = useState(false)
  const [feedbackError, setFeedbackError] = useState<string | null>(null)

  const setTab = useCallback(
    (next: PortalPjTab) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        p.set('tab', next)
        if (next === 'nova' || next === 'nf' || next === 'feedback') p.delete('request')
        return p
      })
    },
    [setSearchParams],
  )

  const openRequest = useCallback(
    (id: string) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        p.set('tab', 'meus')
        p.set('request', id)
        return p
      })
    },
    [setSearchParams],
  )

  const closeRequest = useCallback(() => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      p.delete('request')
      return p
    })
    setRequestDetail(null)
  }, [setSearchParams])

  const loadTypes = useCallback(async () => {
    setTypesLoading(true)
    try {
      const data = await financeService.listTypes()
      setTypes(
        data.map((t) => ({
          key: t.key,
          area: t.area as FinanceAreaKey,
          label: t.label,
          slaHours: t.slaHours,
          mvp: Boolean(t.mvp),
          portal: Boolean(t.portal),
        })),
      )
    } catch {
      toast.error('Não foi possível carregar os tipos de solicitação.')
    } finally {
      setTypesLoading(false)
    }
  }, [])

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true)
    setRequestsError(null)
    try {
      const res = await financeService.listRequests({ page: 1 })
      setRequests(res.data)
    } catch {
      setRequestsError('Não foi possível carregar seus chamados.')
    } finally {
      setRequestsLoading(false)
    }
  }, [])

  const loadRequestDetail = useCallback(
    async (id: string) => {
      setDetailLoading(true)
      try {
        const detail = await financeService.getRequest(id)
        setRequestDetail(detail)
      } catch {
        toast.error('Não foi possível carregar o chamado.')
        closeRequest()
      } finally {
        setDetailLoading(false)
      }
    },
    [closeRequest],
  )

  const loadFeedback = useCallback(async () => {
    setFeedbackLoading(true)
    setFeedbackError(null)
    try {
      const data = await portalPjFeedbackService.listMine()
      setFeedbackItems(data)
    } catch {
      setFeedbackError('Não foi possível carregar seus feedbacks.')
    } finally {
      setFeedbackLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadTypes()
  }, [loadTypes])

  useEffect(() => {
    if (tab === 'meus') void loadRequests()
  }, [tab, loadRequests])

  useEffect(() => {
    if (tab === 'feedback' && canUseFeedback) void loadFeedback()
  }, [tab, canUseFeedback, loadFeedback])

  useEffect(() => {
    if (selectedRequestId) {
      void loadRequestDetail(selectedRequestId)
    } else {
      setRequestDetail(null)
    }
  }, [selectedRequestId, loadRequestDetail])

  const visibleTypes = useMemo(
    () =>
      types.filter(
        (t) => isPortalTypeVisibleForRole(role, t) && isPortalPjCatalogType(t.key),
      ),
    [types, role],
  )

  const typesByArea = useMemo(
    () => buildPortalTypesByArea(role, visibleTypes),
    [role, visibleTypes],
  )

  const selectedCard = selectedType
    ? [...typesByArea.values()].flat().find((t) => t.key === selectedType) ?? null
    : null

  const submitRequest = useCallback(
    async (
      typeKey: PortalTypeKey,
      formData: Record<string, string>,
      linkedEmpresaId?: string,
      linkedDealId?: string,
      attachments?: string[],
    ) => {
      setSubmitting(true)
      try {
        const result = await financeService.createRequest({
          type: typeKey,
          linkedEmpresaId,
          linkedDealId,
          formData,
          attachments,
        })
        toast.success(`Solicitação aberta: ${result.protocol}`)
        setSelectedType(null)
        setTab('meus')
        await loadRequests()
        return result
      } catch {
        toast.error('Falha ao enviar solicitação. Tente novamente.')
        return null
      } finally {
        setSubmitting(false)
      }
    },
    [loadRequests, setTab],
  )

  const addComment = useCallback(
    async (body: string, _isInternal?: boolean, attachments: string[] = []) => {
      if (!selectedRequestId) return false
      try {
        const { comment, requestStatus } = await financeService.addRequestComment(
          selectedRequestId,
          body,
          false,
          attachments,
        )
        setRequestDetail((prev) =>
          prev
            ? {
                ...prev,
                comments: [...prev.comments, comment],
                ...(requestStatus ? { status: requestStatus } : {}),
              }
            : prev,
        )
        return true
      } catch {
        toast.error('Falha ao enviar comentário')
        return false
      }
    },
    [selectedRequestId],
  )

  const reloadRequestDetail = useCallback(() => {
    if (selectedRequestId) void loadRequestDetail(selectedRequestId)
  }, [selectedRequestId, loadRequestDetail])

  const typeLabels = useMemo(
    () => Object.fromEntries(types.map((t) => [t.key, t.label])),
    [types],
  )

  return {
    tab,
    setTab,
    allowedAreas,
    typesLoading,
    typesByArea,
    selectedType,
    setSelectedType,
    selectedCard,
    submitting,
    submitRequest,
    requests,
    requestsLoading,
    requestsError,
    reloadRequests: loadRequests,
    selectedRequestId,
    requestDetail,
    detailLoading,
    openRequest,
    closeRequest,
    addComment,
    reloadRequestDetail,
    canViewFinancialValues: canViewFinanceFinancialValues(role),
    canViewClosedScope: canViewFinanceClosedScope(role),
    typeLabels,
    feedbackItems,
    feedbackLoading,
    feedbackError,
  }
}
